// lib/customer-auth.ts — client-side helpers for the customer account panel.
// Token is stored in localStorage; all calls go through the Next /api proxy to the
// Go backend. Separate from the admin token ("sb_access_token") so the two never clash.
//
// KNOWN RISK — access token in localStorage (do not "fix" casually):
// `sb_customer_token` is readable by any script running on the storefront, and
// the storefront CSP still allows 'unsafe-inline' (see proxy.ts). A single XSS
// therefore reads the token and can call every /api/account/* endpoint the
// customer can — wallet balance, order history, wallet checkout. HttpOnly on the
// refresh cookie does not offset this: the token itself is not in a cookie.
// Moving the access token into an HttpOnly cookie is real work (the backend
// authenticates with a Bearer header on every route, so it needs a cookie-auth
// path plus CSRF defences) and is tracked as separate work — it is deliberately
// NOT attempted here. What changed here: the refresh credential is now split per
// role, so this surface can no longer be used to obtain an ADMIN token.

const TOKEN_KEY = "sb_customer_token";
const USER_KEY = "sb_customer_user";

export interface CustomerUser {
    id: string;
    email: string;
    name: string;
    role: string;
}

/** Machine-readable auth failures; UI copy is localized at the call site. */
export class CustomerAuthError extends Error {
    constructor(
        public readonly code: string,
        public readonly fields?: Record<string, string>,
    ) {
        super(code);
        this.name = "CustomerAuthError";
    }
}

export interface CustomerOrder {
    id: string;
    customer: string;
    email: string;
    /**
     * The two lookup inputs a REPEAT of this order needs (see
     * `lib/subscriptions-api.ts`). Optional because the deployed api v7 does
     * not send them; the account panel hides the "repeat this order" control
     * for an order that arrives without them rather than guessing.
     */
    package_id?: string;
    locale?: string;
    platform: string;
    service: string;
    category?: string;
    sub_category?: string;
    quantity: number;
    price: number;
    /**
     * `price`'ın para birimi: siparişin KATALOG birimi (tr→TRY, diğer→USD).
     *
     * Yoktu. Panel tutarı CÜZDANIN para birimiyle biçimliyordu ve cüzdan hiç
     * yüklenmemişse sunucu onu "TRY" diye döndürüyor — yani $49 ödeyen müşteri
     * kendi siparişini ₺49 olarak görüyordu. api v16 öncesi gelmez.
     */
    price_currency?: string;
    link: string;
    /**
     * The LEGACY five-value column. It cannot tell `partially_completed` from
     * `completed` (audit B23), so nothing that reports progress to a buyer may
     * read it — use `lifecycle_status` through `resolveLifecycle()` instead.
     * Kept because the deployed api v7 still sends only this one.
     */
    status: string;
    /**
     * One of the eleven lifecycle ids, derived by the server on every read.
     * Absent on api v7; `lib/order-lifecycle.ts` falls back honestly.
     */
    lifecycle_status?: string;
    /** quantity − remains, clamped by the server. Absent on api v7. */
    delivered?: number;
    created_at: string;
    // Phase 2 fulfillment / live tracking
    /**
     * true = an order really was placed with a supplier.
     *
     * The panel needs this to read the delivery count safely: `remains` is a
     * plain int on the server, so a never-dispatched order carries 0, and
     * `quantity - remains` would present that order as fully delivered.
     *
     * `provider_order_id` below was meant to answer the same question and
     * could not: the customer whitelist (handler.customerOrderViews)
     * deliberately withholds every supplier field, so it has never been sent to
     * a customer and the guard reading it never fired. api v15 sends this
     * boolean instead — the same answer with none of the supplier's identity.
     * Absent on older api builds.
     */
    delivery_started?: boolean;
    /** Never sent to customers; kept because the admin payload carries it. */
    provider_order_id?: string;
    provider_status?: string;
    /**
     * The follower/like count read BEFORE delivery began.
     *
     * It sat in the database and on the model all along and was simply never
     * forwarded, so a buyer could not reconcile "1.000 delivered" against
     * their own profile. api v27 sends it; older builds omit it, so treat it
     * as unknown rather than as zero — zero is a real, different answer.
     */
    start_count?: number;
    remains?: number;
    synced_at?: string;
    /**
     * When delivery finished — the instant the order's refill window starts
     * counting from (G03: completed_at + the stamped `refill_days`).
     *
     * Absent for two different reasons and they must not be conflated: an
     * order that has not been delivered yet, and a historical order written
     * before the order-events ledger existed. Either way the window is
     * UNKNOWN. Never fall back to `updated_at`: it moves on any later write,
     * so a window derived from it would misstate a written term.
     */
    completed_at?: string;
    /**
     * The refill term this order was SOLD under (G03, owner decisions D-06/D-07):
     * 30, 90 or 365 days by the package's USD list price, stamped by the API when
     * the order was written and never rewritten. `null` on an order written before
     * the policy — it keeps the terms it was sold under and must never be shown a
     * band computed today. Absent on an API older than v30.
     */
    refill_days?: number | null;
    /** "refill-2026-10-01" when stamped; null on a legacy order. */
    refill_policy_version?: string | null;
    /**
     * completed_at + refill_days (UTC), derived by the API; null until delivery
     * finishes and on legacy orders. The panel computes the same instant from
     * `completed_at` and the stamp (lib/refill-policy.ts `refillWindowEndMs`).
     */
    refill_window_ends_at?: string | null;
    /**
     * The last time the row was written — ANY write, including an operator
     * touching a note. It is shown as "last movement" and is deliberately never
     * used to derive a right: the refill window reads `completed_at` and the
     * order's stamped term, nothing else (see CTO-GUNCELLEME madde 1).
     */
    updated_at?: string;
    /**
     * The buyer's own note. The server only forwards it when the order belongs
     * to a registered customer (`order_handler.go:417`), because the column is
     * shared with operator notes on guest orders.
     */
    note?: string;
    /**
     * Where the money actually is, as `models.PaymentState`
     * (backend_combined/internal/models/payment.go:16-24): legacy_unverified,
     * pending, awaiting_payment, verification_pending, paid, failed, canceled,
     * refunded, reconciliation_required.
     *
     * Whitelisted since api v16 (`order_handler.go:394`) and simply never
     * declared here, so every caller had to read it through a cast. It answers
     * the question `status` cannot: an order can be `pending` because nobody
     * paid, or `pending` because the money arrived and nothing has been
     * dispatched yet, and those two need different words in front of a customer.
     */
    payment_state?: string;
    /** How it was paid (`wallet`, `stripe`, …). Whitelisted with `payment_state`. */
    payment_method?: string;
}

export function getCustomerToken(): string | null {
    if (typeof window === "undefined") return null;
    return localStorage.getItem(TOKEN_KEY);
}

export function getCustomerUser(): CustomerUser | null {
    if (typeof window === "undefined") return null;
    try {
        const raw = localStorage.getItem(USER_KEY);
        return raw ? (JSON.parse(raw) as CustomerUser) : null;
    } catch {
        return null;
    }
}

function store(token: string, user: CustomerUser) {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(USER_KEY, JSON.stringify(user));
    // Notify listeners (e.g. header) in the same tab.
    window.dispatchEvent(new Event("sb-customer-auth"));
}

/**
 * Sign the customer out.
 *
 * Clearing the two localStorage keys is not a logout on its own: the refresh
 * cookie and its `refresh_tokens` row live for the full refresh TTL, so a
 * "logged out" session on a shared computer could be revived from the cookie
 * alone. So this also asks the server to revoke the stored refresh tokens and
 * clear every refresh cookie.
 *
 * The call is best-effort and deliberately not awaited — the caller navigates
 * away immediately — so it is sent with `keepalive` to survive the unload. Local
 * state is cleared either way: a customer who clicked "log out" must never be
 * left looking signed in because the network failed.
 *
 * It is sent WITHOUT requiring a local access token. The access token lives ~15
 * minutes and the refresh cookie for days, so the most common logout — someone
 * returning to a shared computer, and every logout triggered BY a 401 — happens
 * with no usable token in hand. `/api/auth/logout` therefore no longer sits
 * behind the auth middleware: it identifies the session from whichever
 * credential the request carries (this header when there is one, otherwise the
 * HttpOnly refresh cookie the browser attaches) and clears every refresh cookie.
 * `credentials: "include"` is what makes the cookie-only path work at all.
 */
export function customerLogout() {
    const token = getCustomerToken();
    if (typeof window !== "undefined") {
        try {
            void fetch("/api/auth/logout", {
                method: "POST",
                // Only sent when we actually hold one; the server falls back to the
                // refresh cookie, which is the case that used to be refused.
                headers: token ? { Authorization: `Bearer ${token}` } : undefined,
                credentials: "include",
                keepalive: true,
            }).catch(() => { });
        } catch {
            // Never let a failed revoke keep the customer signed in locally.
        }
    }
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    if (typeof window !== "undefined") window.dispatchEvent(new Event("sb-customer-auth"));
}

/**
 * Exchange the HttpOnly customer refresh cookie (`sb_customer_refresh`, and the
 * retired shared `refresh_token` during the migration window) for a fresh access
 * token.
 *
 * `/api/auth/refresh` is the STOREFRONT session family only. An administrator's
 * cookie presented here is refused with `REFRESH_SCOPE_MISMATCH` — the admin
 * panel renews on `/api/auth/admin/refresh` instead — which is what stops a
 * storefront script from turning an admin's presence in the same browser into an
 * admin access token.
 *
 * The access token lives ~15 minutes and nothing on the customer side renewed it,
 * so a 3DS challenge or a slow card form could outlive it — the card was charged
 * and the credit call that follows came back 401. Callers on that path must be
 * able to renew and retry instead of losing the payment.
 *
 * Returns null when the cookie is missing or rejected (the caller then asks the
 * customer to log in again — the pending top-up is retried after that). The
 * cookie is `Secure`, so over plain http (local dev) it is never sent and this
 * always degrades to null rather than pretending to have refreshed.
 */
export async function refreshCustomerToken(): Promise<string | null> {
    try {
        const res = await fetch("/api/auth/refresh", { method: "POST", credentials: "include" });
        if (!res.ok) return null;
        const json = await res.json().catch(() => ({}));
        const token: unknown = json?.data?.access_token;
        if (typeof token !== "string" || !token) return null;
        localStorage.setItem(TOKEN_KEY, token);
        window.dispatchEvent(new Event("sb-customer-auth"));
        return token;
    } catch {
        return null;
    }
}

async function authCall(path: string, body: unknown): Promise<{ user: CustomerUser; token: string }> {
    const res = await fetch(path, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
        throw new CustomerAuthError(
            typeof json?.error?.code === "string" ? json.error.code : "AUTH_FAILED",
            json?.error?.fields && typeof json.error.fields === "object" ? json.error.fields : undefined,
        );
    }
    const user: CustomerUser = json?.data?.user;
    const token: string = json?.data?.tokens?.access_token;
    if (!token || !user) throw new CustomerAuthError("UNEXPECTED_RESPONSE");
    store(token, user);
    return { user, token };
}

export function customerLogin(email: string, password: string) {
    return authCall("/api/auth/login", { email, password });
}

export function customerRegister(name: string, email: string, password: string) {
    return authCall("/api/auth/register", { name, email, password });
}

/**
 * One page of the customer's own orders, with the server's count beside it.
 *
 * `GET /api/account/orders` has accepted `page` and `limit` and answered with
 * `meta{total,page,page_size}` since api v16 (`order_handler.go:296-318`), and
 * nothing in the panel ever sent either parameter. The server's default page
 * size is 50, so order 51 onwards simply did not exist for the customer and
 * the panel said nothing about it.
 *
 * `total` is what makes the list honest: a screen can say "50 of 63" instead of
 * showing 50 rows and implying that is all there is. An older build that omits
 * `meta` reports `total` as the number of rows actually received, which is the
 * truthful reading of "we do not know of any more".
 */
export interface CustomerOrdersPage {
    orders: CustomerOrder[];
    /** The server's count of ALL this customer's orders, not of this page. */
    total: number;
    page: number;
    pageSize: number;
}

/**
 * A refusal from the orders endpoint, as a code rather than a sentence.
 *
 * The previous `throw new Error("Siparişler alınamadı")` put a hard-coded
 * Turkish sentence in a file that five locales also go through. It was never
 * rendered — every caller discards the message — but the next caller to render
 * it would have shipped Turkish to a French customer. The code is
 * machine-readable; the copy is chosen at the call site, in the caller's locale.
 */
export class CustomerOrdersError extends Error {
    constructor(
        public readonly code: string,
        public readonly status: number,
    ) {
        super(code);
        this.name = "CustomerOrdersError";
    }
}

export async function getMyOrdersPage(
    page = 1,
    limit = 50,
    options: { signal?: AbortSignal } = {},
): Promise<CustomerOrdersPage> {
    const token = getCustomerToken();
    if (!token) return { orders: [], total: 0, page: 1, pageSize: limit };

    // Clamped to what the handler accepts (`limit < 1 || limit > 100` is reset
    // to 50 server-side), so a caller's mistake cannot silently change the page
    // size out from under the "{loaded} of {total}" counter.
    const safePage = Number.isFinite(page) && page >= 1 ? Math.floor(page) : 1;
    const safeLimit = Number.isFinite(limit) ? Math.min(100, Math.max(1, Math.floor(limit))) : 50;

    const res = await fetch(`/api/account/orders?page=${safePage}&limit=${safeLimit}`, {
        headers: { Authorization: `Bearer ${token}` },
        signal: options.signal,
    });
    if (!res.ok) {
        if (res.status === 401) customerLogout();
        const body = await res.json().catch(() => ({}));
        const code = typeof body?.error?.code === "string" ? body.error.code : `HTTP_${res.status}`;
        throw new CustomerOrdersError(code, res.status);
    }
    const json = await res.json().catch(() => ({}));
    const orders: CustomerOrder[] = Array.isArray(json?.data?.orders) ? json.data.orders : [];
    const meta = json?.data?.meta;
    return {
        orders,
        // Never `orders.length || 0` when meta is present: a short last page
        // would then report a total smaller than what has already been loaded.
        total: typeof meta?.total === "number" ? meta.total : orders.length,
        page: typeof meta?.page === "number" ? meta.page : safePage,
        pageSize: typeof meta?.page_size === "number" ? meta.page_size : safeLimit,
    };
}

/**
 * One order, by id.
 *
 * Returns `null` for "not found" — which the server deliberately cannot
 * distinguish from "not yours", because answering 403 for somebody else's
 * order would confirm the id is real and turn the route into an oracle for
 * guessing order ids.
 *
 * `null` ALSO comes back when the endpoint itself is missing: it landed in api
 * v28 and this site deploys web before api, so for one deploy window the page
 * exists and the route does not. Both cases are a 404 with no way to tell them
 * apart from the outside, which is why the caller falls back to walking the
 * list rather than treating `null` as final.
 */
export async function getMyOrder(
    id: string,
    options: { signal?: AbortSignal } = {},
): Promise<CustomerOrder | null> {
    const token = getCustomerToken();
    if (!token || !id) return null;

    const res = await fetch(`/api/account/orders/${encodeURIComponent(id)}`, {
        headers: { Authorization: `Bearer ${token}` },
        signal: options.signal,
    });
    if (res.status === 404) return null;
    if (!res.ok) {
        if (res.status === 401) customerLogout();
        const body = await res.json().catch(() => ({}));
        const code = typeof body?.error?.code === "string" ? body.error.code : `HTTP_${res.status}`;
        throw new CustomerOrdersError(code, res.status);
    }
    const json = await res.json().catch(() => ({}));
    const order = json?.data?.order;
    return order && typeof order === "object" ? (order as CustomerOrder) : null;
}

/**
 * The first page of orders.
 *
 * SIGNATURE DELIBERATELY UNCHANGED. `AccountShell` and `AccountPanel` both call
 * it with no arguments and read a bare array; adding a parameter or an envelope
 * here would break two files this package does not own. It is now a thin call
 * on `getMyOrdersPage`, so the two cannot drift.
 */
export async function getMyOrders(): Promise<CustomerOrder[]> {
    const { orders } = await getMyOrdersPage();
    return orders;
}

// ── Wallet (Phase 3) ─────────────────────────────────────────────
export interface WalletTx {
    id: string;
    type: string; // deposit, debit, refund, bonus
    amount: number;
    balance_after: number;
    description: string;
    order_id?: string;
    created_at: string;
}
export interface WalletTier {
    name: string;
    cashback_pct: number;
    discount_pct: number;
    min: number;
    next: number; // 0 = top tier
}
export interface WalletSummary {
    balance: number;
    currency: string;
    lifetime_spend: number;
    tier: WalletTier;
    transactions: WalletTx[];
}

export async function getWallet(): Promise<WalletSummary | null> {
    const token = getCustomerToken();
    if (!token) return null;
    const res = await fetch("/api/account/wallet", { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) {
        if (res.status === 401) customerLogout();
        return null;
    }
    const json = await res.json().catch(() => ({}));
    return (json?.data as WalletSummary) ?? null;
}

export interface WalletCheckoutBody {
    package_id: string;
    platform: string;
    service: string;
    category?: string;
    sub_category?: string;
    link: string;
    locale?: string;
    note?: string;
}

/**
 * Pay for an order entirely from the wallet.
 *
 * Every refusal is thrown as a CODE, never as a sentence — the same rule as
 * `CustomerOrdersError` above, and for the same reason: this file is reached
 * from six locales, so a Turkish string thrown here is a Turkish string shown
 * to a French customer. It replaces three of them: a hard-coded
 * "Giriş gerekli", a hard-coded "Ödeme başarısız", and `error.message`
 * straight off the server, which is Turkish operator copy in every locale.
 *
 * The codes: AUTH_REQUIRED (no session), INSUFFICIENT_FUNDS (402 — kept
 * spelled exactly as before so existing handling still matches), and whatever
 * `error.code` the backend sent, falling back to WALLET_CHECKOUT_FAILED. The
 * caller picks the sentence, in the caller's language.
 *
 * NOTE for whoever wires this up: it currently has no callers. The checkout
 * screen pays from the wallet through `walletCheckoutAny()` in
 * app/[locale]/checkout/checkout-v2.ts, not through this function.
 */
export async function walletCheckout(body: WalletCheckoutBody): Promise<{ orderId: string; balance: number }> {
    const token = getCustomerToken();
    if (!token) throw new Error("AUTH_REQUIRED");
    const res = await fetch("/api/account/wallet/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
        if (res.status === 402) throw new Error("INSUFFICIENT_FUNDS");
        const code = json?.error?.code;
        throw new Error(typeof code === "string" && code ? code : "WALLET_CHECKOUT_FAILED");
    }
    return { orderId: json?.data?.orderId, balance: json?.data?.balance ?? 0 };
}

/**
 * A wallet endpoint refused the call. `status` and `code` let the caller act on
 * the refusal (renew the token on 401, show the real minimum on
 * AMOUNT_BELOW_MINIMUM) instead of turning every failure into one opaque string.
 */
export class WalletApiError extends Error {
    constructor(
        public readonly status: number,
        public readonly code: string,
        message: string,
        /** Server-owned minimum top-up, in the wallet currency (AMOUNT_BELOW_MINIMUM only). */
        public readonly minimum?: number,
        public readonly currency?: string,
    ) {
        super(message);
        this.name = "WalletApiError";
    }
}

function walletErrorFrom(status: number, json: unknown): WalletApiError {
    const err = (json as { error?: { code?: unknown; message?: unknown; fields?: Record<string, unknown> } })?.error;
    const minimum = typeof err?.fields?.minimum === "number" ? err.fields.minimum : undefined;
    const currency = typeof err?.fields?.currency === "string" ? err.fields.currency : undefined;
    return new WalletApiError(
        status,
        typeof err?.code === "string" ? err.code : "WALLET_ERROR",
        typeof err?.message === "string" && err.message ? err.message : "",
        minimum,
        currency,
    );
}

export interface WalletTopupIntent {
    clientSecret: string;
    /** The exact figure the card will be charged, as the server resolved it. */
    amount: number;
    currency: string;
    /** Server-owned minimum; absent on api v7, which does not send it. */
    minimum?: number;
}

// Create a Stripe PaymentIntent to add real funds. Returns clientSecret for Elements.
// `amount` is the server's figure, which the caller MUST show before the card is
// charged — an older backend still coerces a below-minimum request upward.
export async function walletTopupIntent(amount: number, currency?: string): Promise<WalletTopupIntent> {
    const token = getCustomerToken();
    if (!token) throw new WalletApiError(401, "UNAUTHORIZED", "");
    const res = await fetch("/api/account/wallet/topup/intent", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ amount, currency }),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) throw walletErrorFrom(res.status, json);
    return {
        clientSecret: json?.data?.clientSecret,
        amount: typeof json?.data?.amount === "number" ? json.data.amount : amount,
        currency: json?.data?.currency ?? currency ?? "",
        minimum: typeof json?.data?.minimum === "number" ? json.data.minimum : undefined,
    };
}

export interface WalletTopupCredit {
    balance: number;
    /** false when this intent had already been credited (idempotent replay). */
    credited: boolean;
}

// Confirm a succeeded PaymentIntent → backend verifies + credits the wallet.
// Safe to call again for the same intent: the backend keys the credit on the
// PaymentIntent id, so a retry after a failed attempt cannot double-credit.
export async function walletTopupConfirm(paymentIntentId: string): Promise<WalletTopupCredit> {
    const token = getCustomerToken();
    if (!token) throw new WalletApiError(401, "UNAUTHORIZED", "");
    const res = await fetch("/api/account/wallet/topup/confirm", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ payment_intent_id: paymentIntentId }),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) throw walletErrorFrom(res.status, json);
    return {
        balance: typeof json?.data?.balance === "number" ? json.data.balance : 0,
        // api v7 does not send `credited`; a 200 there means the credit went through.
        credited: json?.data?.credited !== false,
    };
}
